import React, { useContext, useMemo, useRef } from 'react';
import { ThemeContext } from 'styled-components';

// Contador modulo-level pra gerar ids unicos dos linearGradients de
// cada instancia (React 17 nao tem useId). UseRef trava o valor depois
// do primeiro render, garantindo estabilidade entre renders.
let sparklineIdCounter = 0;

// Sparkline minimalista — SVG puro. Reescrito do recharts pra eliminar
// o warning 'Line: prop type points[0].value must be a function' que
// vem da definicao malformada de propTypes do recharts 1.x.
//
// Bonus: muito mais leve (sem recharts overhead) e mais rapido em
// renders frequentes (alimentamos buffer a cada segundo via subscription).
//
// Props (API mantida):
// - data: array de numeros OU array de objetos { value: number, ts?: number }
// - width / height: dimensoes em px (default 80x24)
// - color: cor da linha (default: theme.lightblue)
// - fill: se true desenha area abaixo da linha (gradient sutil)
//
// Renderiza null se nao houver pelo menos 2 pontos validos.
const Sparkline = ({
  data,
  width = 80,
  height = 24,
  color,
  fill = false,
}) => {
  const { colors } = useContext(ThemeContext);
  const lineColor = color || colors.lightblue;
  // Id unico estavel pra cada instancia de Sparkline (varios na pagina
  // precisam de defs separados pra nao colidir). Lazy init via useRef
  // para incrementar o counter so na primeira render.
  const idRef = useRef(null);
  if (idRef.current === null) {
    sparklineIdCounter += 1;
    idRef.current = `sparkline-grad-${sparklineIdCounter}`;
  }
  const gradientId = idRef.current;

  // Normaliza pra array de valores numericos finitos. Number.isFinite
  // rejeita NaN/Infinity que sao numerics mas quebram qualquer chart.
  const values = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const result = [];
    data.forEach((d) => {
      let v;
      if (Number.isFinite(d)) v = d;
      else if (d && Number.isFinite(d.value)) v = d.value;
      else {
        const parsed = parseFloat(d);
        if (Number.isFinite(parsed)) v = parsed;
      }
      if (v !== undefined) result.push(v);
    });
    return result;
  }, [data]);

  if (values.length < 2) return null;

  // Escala vertical: mapeia valor -> Y em pixels, com 10% de padding
  // no topo/base pra a linha nao colar nas bordas.
  const min = Math.min(...values);
  const max = Math.max(...values);
  const valueRange = max === min ? 1 : max - min;
  const pad = valueRange * 0.1;
  const padHeight = 2; // px de margem do canvas
  const usableHeight = height - padHeight * 2;
  const yFor = (v) => {
    const normalized = (v - (min - pad)) / (valueRange + pad * 2);
    return padHeight + (1 - normalized) * usableHeight;
  };

  // Escala horizontal: pontos distribuidos uniformemente ao longo da
  // largura disponivel.
  const padWidth = 2;
  const usableWidth = width - padWidth * 2;
  const xFor = (i) =>
    padWidth + (values.length === 1 ? 0 : (i / (values.length - 1)) * usableWidth);

  // Path 'd' string: M no primeiro ponto, L em cada um depois.
  const linePath = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(2)},${yFor(v).toFixed(2)}`)
    .join(' ');

  // Path de area (mesma linha + retorno pelo eixo X) — pra fill gradient.
  const areaPath = fill
    ? `${linePath} L${xFor(values.length - 1).toFixed(2)},${height} L${xFor(0).toFixed(2)},${height} Z`
    : null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
    >
      {fill && (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity={0.3} />
            <stop offset="100%" stopColor={lineColor} stopOpacity={0.02} />
          </linearGradient>
        </defs>
      )}
      {fill && <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />}
      <path
        d={linePath}
        fill="none"
        stroke={lineColor}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default Sparkline;
